import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import 'rxjs/add/operator/mergeMap';

import { PlatformService } from 'buzzyears.ui';
import { IWorkflow, Workflow, WorkflowVisibilityType } from 'component.workflow.core';
import { ConciseContextTree, ContextTree } from 'buzzyears.core';
import { ApiService } from './api.service';
import { WorkflowEdgeService } from './workflow-edge.service';
import { WorkflowVertexService } from './workflow-vertex.service';

@Injectable()
export class WorkflowService extends ApiService {

  constructor(http: HttpClient,
              platformService: PlatformService,
              protected edgerService: WorkflowEdgeService,
              protected vertexService: WorkflowVertexService) {
    super(http, platformService);
  }

  moveToFolder(folderId: string, workflowIds: string[]): Observable<any> {
    return this.http
      .post(this.toUrl('move-to-folder'), {
        folderId: folderId,
        workflowIds: workflowIds
      }, this.requestOptions)
      .catch(this.manageError);
  }

  getInFolder(folderId: string): Observable<any> {

    const root = !folderId,
      filter = root ? {
        $or: [
          {parentFolderId: null},
          {parentFolderId: ''}
        ]
      } : {parentFolderId: folderId};

    return this.http
      .post(this.toUrl('search'), {
        filter: filter,
        paging: {limit: 999999},
        sort: {
          'properties.sortOrder': 'asc',
          createdAt: 'asc',
          _id: 'asc'
        }
      }, this.requestOptions)
      .map((raw: any) => raw.docs.map(r => new Workflow(r)))
      .catch(this.manageError);
  }

  fetchInternals(select: any = {}): Observable<any> {
    return this.http
      .post(this.toUrl('fetch'), {
        filter: {
          'visibilityType': {$exists: true,
            $in: [WorkflowVisibilityType.InternalOnly, WorkflowVisibilityType.Both]}
        },
        paging: {limit: 999999},
        sort: {
          createdAt: 'asc',
          _id: 'asc'
        },
        select
      }, this.requestOptions)
      .map((raw: any) => raw.docs.map(w => w))
      .catch(this.manageError);
  }

  fetchInternalNames(): Observable<any> {
    return this.http
      .post(this.toUrl('search'), {
        filter: {
          'visibilityType': {$exists: true,
            $in: [WorkflowVisibilityType.InternalOnly, WorkflowVisibilityType.Both]}
        },
        paging: {limit: 999999},
        sort: {
          createdAt: 'asc',
          _id: 'asc'
        },
        select: { id: 1, name: 1}
      }, this.requestOptions)
      .map((raw: any) => raw.docs.map(w => w))
      .catch(this.manageError);
  }

  fetchByIds(ids: string[]): Observable<any> {
    return this.http
      .post(this.toUrl('fetch'), {
        filter: {
          _id: { $in: ids}
        },
        paging: {limit: 999999},
        sort: {
          createdAt: 'asc',
          _id: 'asc'
        }
      }, this.requestOptions)
      .map((raw: any) => raw.docs.map(w => w))
      .catch(this.manageError);
  }

  fetch(): Observable<any> {
    return this.http
      .post(this.toUrl('fetch'), {
        paging: {limit: 999999},
        sort: {
          createdAt: 'asc',
          _id: 'asc'
        }
      }, this.requestOptions)
      .map((raw: any) => raw.docs.map(w => w))
      .catch(this.manageError);
  }

  list(): Observable<any> {
    return this.http
      .post(this.toUrl('search'), {
        paging: {limit: 999999},
        sort: {
          createdAt: 'asc',
          _id: 'asc'
        }
      }, this.requestOptions)
      .map((raw: any) => raw.docs.map(w => new Workflow(w)))
      .catch(this.manageError);
  }

  duplicate(id: string): Observable<any> {
    return this.get(id)
      .mergeMap(workflow => {
        workflow.name = workflow.name + ' (' + ((new Date()).toLocaleString()) + ')';
        workflow.duplicateOf = id;

        return this.shallowClone(workflow);
      })
      .mergeMap(clonedWorkflow => {
        return forkJoin(
          this.edgerService.duplicateFromToWorkflow(id, clonedWorkflow.id),
          this.vertexService.duplicateFromToWorkflow(id, clonedWorkflow.id)
        );
      });
  }

  shallowClone(data: IWorkflow): Observable<any> {
    return this.http
      .post(this.toUrl('clone'), data, this.requestOptions)
      .catch(this.manageError);
  }

  update(data: IWorkflow): Observable<any> {
    return this.http
      .put(this.toUrl(), data, this.requestOptions)
      .catch(this.manageError);
  }

  remove(id: string): Observable<any> {
    return this.http
      .delete(this.toUrl(id), this.requestOptions)
      .catch(this.manageError);
  }

  get(id: string): Observable<any> {
    return this.http
      .get(this.toUrl(id), this.requestOptions)
      .catch(this.manageError);
  }

  create(workflow: IWorkflow): Observable<any> {
    return this.http
      .post(this.toUrl(), workflow, this.requestOptions)
      .catch(this.manageError);
  }

  protected toUrl(...parts: string[]): string {
    return super.toUrl('workflow', ...parts);
  }

  contextTree(): Observable<ContextTree> {
    return this.platformService.getContext(this.platformService.activeUserId).map(raw => new ConciseContextTree(raw));
  }
}



// WEBPACK FOOTER //
// ./src/app/services/workflow.service.ts